import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Accordion,
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Input,
  Progress,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/react';
import {
  ArrowLeft,
  ArrowRight,
  ArrowsClockwise,
  CheckCircle,
  DownloadSimple,
  FileArrowUp,
  FileDoc,
  LockOpen,
  Sparkle,
  Warning,
  WarningCircle,
} from 'phosphor-react';
import { toast } from 'sonner';
import DialogModal from '../../components/DialogModal';
import { PageBox } from '../../components/pageBox';
import { useEventos } from '../../hooks/useEventos';
import { useKanban } from '../../hooks/useKanban';
import {
  ICampoFicha,
  IItemEscopo,
  IResultado,
  ISecaoFicha,
  IVersaoDocumento,
  mensagemDoErro,
  useAcoesContrato,
  useFicha,
  useTarefa,
  useTemplateDoEvento,
} from '../../hooks/useEventoContrato';
import BlocoFicha from './components/contrato/BlocoFicha';
import CampoFicha from './components/contrato/CampoFicha';
import DockContrato, { Salvamento } from './components/contrato/DockContrato';
import TabelaEscopo from './components/contrato/TabelaEscopo';
import SecoesDoNegocio from './components/contrato/SecoesDoNegocio';
import {
  PREFIXO_SECAO,
  editadaNaTela,
  estadoDaSecao,
  mesmoTexto,
  papelDaSecao,
  secaoVazia,
  secoesParaSalvar,
  textoDaSecao,
} from './components/contrato/secoesDaFicha';
import { somaDoEscopo } from './components/contrato/dinheiro';
import TemplateDoEvento from './components/contrato/TemplateDoEvento';
import { TIPO_CONTRATO_PADRAO, rotuloDoTipo } from './components/contrato/tiposDeContrato';

/**
 * O texto que o jurídico mandava no Claude web a cada fornecedor, com os rótulos
 * e sem os dados — igual para todos (decisão do user, 11/09). Abre já digitado
 * na ficha sem texto; o operador só completa depois dos dois-pontos.
 */
const TEXTO_BASE = [
  'Utilize este e-mail: ',
  'Administrador: ',
  'Dados Bancários: ',
  'BANCO: ',
  'AG: ',
  'CC: ',
  'CNPJ: ',
  'Chave PIX: ',
  'Local: ',
  'Data do evento: ',
].join('\n');

/** Texto base intacto não é fonte: salva vazio, e a IA não lê rótulo sem dado. */
const semTextoBase = (texto: string) => (texto.trim() === TEXTO_BASE.trim() ? '' : texto);

/** Data e hora do Word corrigido e da liberação, no fuso do navegador. */
const quando = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '';

/** O aviso do passo 3. Liberado não é "sem bloqueio": o ponto apontado continua no documento. */
const avisoDoGate = (r: IResultado) => {
  // Com o documento para assinatura corrigido, vale o veredito dele: as partes não vão para assinatura.
  const corrigido = !!r.pacote?.revisado_em;
  if (corrigido ? !r.pacote?.aprovado : !r.aprovado) {
    return {
      status: 'error' as const,
      icone: WarningCircle,
      texto: 'Não envie para assinatura ainda — os pontos abaixo travam o envio.',
    };
  }
  // Tipo do contrato trocado depois de gerar: os documentos são do modelo do tipo anterior.
  if (r.pacote?.desatualizado && r.pacote.desatualizado_por === 'tipo') {
    return {
      status: 'warning' as const,
      icone: Warning,
      texto: 'O tipo do contrato mudou depois de gerado: gere o contrato de novo antes de enviar.',
    };
  }
  // Documento de antes da troca do orçamento: o envio fica barrado até refazer.
  if (r.pacote?.desatualizado) {
    return {
      status: 'warning' as const,
      icone: Warning,
      texto: 'O orçamento mudou depois do documento para assinatura: refaça antes de enviar.',
    };
  }
  if (r.pacote?.erro) {
    return {
      status: 'warning' as const,
      icone: Warning,
      texto: `O documento para assinatura não saiu: ${r.pacote.erro}`,
    };
  }
  const liberados = corrigido ? [] : r.documentos.filter(d => d.liberado_em);
  if (corrigido ? r.pacote?.liberado_em : liberados.length) {
    const pontos = corrigido
      ? r.pacote?.bloqueios_liberados?.length || 1
      : liberados.reduce((n, d) => n + (d.bloqueios_liberados?.length || 1), 0);
    return {
      status: 'warning' as const,
      icone: LockOpen,
      texto: `Envio liberado pelo jurídico com ${pontos} ponto(s) que a conferência apontou — eles continuam no documento. Confira antes de enviar para assinatura.`,
    };
  }
  return {
    status: 'success' as const,
    icone: CheckCircle,
    texto:
      'Nenhum dado do fornecedor anterior sobrou. Confira os documentos antes de enviar para assinatura.',
  };
};

/** O bloco do escopo não é um papel da ficha; é o id dele no acordeão e no "ir para". */
const ESCOPO = 'escopo';
const SECOES = 'secoes';
/** Tempo sem digitar até salvar sozinho. */
const ESPERA_SALVAR_MS = 1200;
/** O documento para assinatura inteiro no lugar do índice de um documento (Word corrigido e liberar). */
const PACOTE = -1;

/**
 * Contrato do fornecedor: o template do EVENTO preenchido com a ficha deste
 * fornecedor.
 *
 * O jurídico gerava esses contratos no Claude web: subia os templates uma vez
 * e, a cada fornecedor, mandava o orçamento e um texto com e-mail,
 * administrador e dados bancários. Aqui é o mesmo gesto — as fontes entram, a
 * IA monta a ficha com o TRECHO de onde tirou cada valor, e gerar é
 * determinístico: corrigir um campo e gerar de novo não chama IA.
 *
 * A ficha SALVA SOZINHA (1,2 s depois da última tecla, ao sair da tela e antes
 * de ler/gerar). Até 10/09 só salvava por um botão que aparecia no fim da ficha
 * depois de editar — voltar ao kanban antes dele perdia a edição em silêncio.
 *
 * Query param (`?evento=&contrato=`) e não path param: dois params no path são
 * armadilha no `hasPermission` (ver a rota). O evento em si tem página própria
 * (`/eventos/:id`, com a aba Kanban em `?aba=kanban`).
 */
export default function EventoContratoPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const eventoId = params.get('evento') ? Number(params.get('evento')) : null;
  const contratoId = params.get('contrato') ? Number(params.get('contrato')) : null;

  const queryClient = useQueryClient();
  const { uploadDocumento } = useEventos();
  const { getAnexos } = useKanban();
  const { data, isLoading, isError, dataUpdatedAt } = useFicha(eventoId, contratoId);
  const ficha = data?.data;
  const campos = ficha?.campos ?? [];
  // O tipo de contrato decide com quais templates do evento este contrato gera.
  const { data: templateData } = useTemplateDoEvento(eventoId);
  const tipoDoContrato = rotuloDoTipo(
    templateData?.data?.tipos,
    ficha?.contrato_tipo ?? TIPO_CONTRATO_PADRAO,
  );
  const {
    salvar,
    lerFontes,
    gerar,
    refazerDocumento,
    enviarRevisado,
    liberar,
    enviarRevisadoPacote,
    liberarPacote,
    invalidar,
  } = useAcoesContrato(eventoId, contratoId);

  const [texto, setTexto] = useState(TEXTO_BASE);
  const [cnpj, setCnpj] = useState('');
  const [edicoes, setEdicoes] = useState<Record<string, string>>({});
  const [itens, setItens] = useState<IItemEscopo[]>([]);
  const [itensEditados, setItensEditados] = useState(false);
  // Seção do negócio reescrita na tela: chave -> texto (um parágrafo por linha).
  const [secoesEditadas, setSecoesEditadas] = useState<Record<string, string>>({});
  const [fechados, setFechados] = useState<string[]>([]);
  const [templateAberto, setTemplateAberto] = useState(false);
  const [enviando, setEnviando] = useState<string | null>(null);
  // Ler/gerar em andamento — inclusive o salvamento que vem antes. O botão só
  // travava depois dele, e um clique duplo disparava dois POSTs.
  const [ocupado, setOcupado] = useState(false);
  const ocupadoRef = useRef(false);
  // Word corrigido e liberação do envio (passo 3): o documento e a versão vista no clique.
  const wordRef = useRef<HTMLInputElement>(null);
  const [revisando, setRevisando] = useState<IVersaoDocumento | null>(null);
  const [liberando, setLiberando] = useState<IVersaoDocumento | null>(null);
  const [confirmarGerar, setConfirmarGerar] = useState(false);
  // Ação que remonta o documento para assinatura corrigido: espera a confirmação.
  const [remontando, setRemontando] = useState<(() => void) | null>(null);
  const refOrcamento = useRef<HTMLInputElement>(null);
  const refCartao = useRef<HTMLInputElement>(null);

  // ── Salvamento automático ─────────────────────────────────────────────
  // `versao` conta as edições; `versaoSalva`, até onde o servidor já tem. O que
  // chega durante um salvamento em voo fica pendente e sai no próximo.
  const [salvamento, setSalvamento] = useState<Salvamento>('salvo');
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [tique, setTique] = useState(0);
  const versao = useRef(0);
  const versaoSalva = useRef(0);
  const atual = useRef({ texto, cnpj, edicoes, itens, itensEditados, secoesEditadas });
  atual.current = { texto, cnpj, edicoes, itens, itensEditados, secoesEditadas };
  const temPendente = () => versao.current !== versaoSalva.current;

  const mexeu = () => {
    versao.current += 1;
    setSalvamento('pendente');
    setTique(t => t + 1);
  };

  const salvarUmaVez = async (): Promise<boolean> => {
    const alvo = versao.current;
    if (alvo === versaoSalva.current) {
      setSalvamento('salvo');
      return true;
    }
    const a = atual.current;
    const secoesMudadas = secoesParaSalvar(a.secoesEditadas, ficha?.secoes ?? []);
    setSalvamento('salvando');
    try {
      await salvar.mutateAsync({
        texto: semTextoBase(a.texto),
        cnpj: a.cnpj,
        valores: a.edicoes,
        ...(a.itensEditados ? { itens: a.itens } : {}),
        ...(Object.keys(secoesMudadas).length ? { secoes: secoesMudadas } : {}),
      });
      versaoSalva.current = Math.max(versaoSalva.current, alvo);
      setSalvoEm(new Date());
      setSalvamento(temPendente() ? 'pendente' : 'salvo');
      return true;
    } catch (e) {
      setSalvamento('erro');
      toast.error(mensagemDoErro(e, 'Não foi possível salvar a ficha.'));
      return false;
    }
  };
  // Um salvamento por vez: dois PUTs juntos podiam chegar fora de ordem, e o mais
  // velho vencia no servidor com o dock dizendo "Salvo".
  const fila = useRef<Promise<unknown>>(Promise.resolve());
  const salvarAgora = (): Promise<boolean> => {
    const vez = fila.current.then(salvarUmaVez);
    fila.current = vez;
    return vez;
  };
  const salvarRef = useRef(salvarAgora);
  salvarRef.current = salvarAgora;

  useEffect(() => {
    if (!tique) return undefined;
    const t = setTimeout(() => salvarRef.current(), ESPERA_SALVAR_MS);
    return () => clearTimeout(t);
  }, [tique]);

  // Saiu da tela por outro caminho (menu, link) com edição pendente: salva igual.
  useEffect(
    () => () => {
      if (temPendente()) salvarRef.current();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Fechar a aba antes de salvar: o navegador pergunta.
  useEffect(() => {
    const aviso = (e: BeforeUnloadEvent) => {
      if (!temPendente()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', aviso);
    return () => window.removeEventListener('beforeunload', aviso);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A tela reflete o servidor sempre que chega dado NOVO dele — leitura, geração, o
  // próprio salvamento, a volta à tela — menos com edição local pendente. Só em
  // `lido_em`/`gerado_em` (até 10/09), voltar logo depois de sair mostrava o cache
  // velho, e a próxima edição regravava no servidor o texto antigo.
  useEffect(() => {
    if (!ficha || temPendente()) return;
    // Servidor sem texto: fica o que a tela já tem se também é "sem texto" (o base, ou
    // vazio porque o operador apagou para colar) — repor o base na recarga fazia ele
    // colar por cima dos rótulos.
    setTexto(local => ficha.fontes.texto || (semTextoBase(local) ? TEXTO_BASE : local));
    setCnpj(ficha.fontes.cnpj);
    setItens(ficha.itens);
    setEdicoes({});
    setItensEditados(false);
    // Seção que o servidor tem igual à da tela fica como o operador digitou: o salvamento tira
    // linha vazia e espaço do fim, e trocar o texto apagava o Enter recém-dado e jogava o cursor
    // para o fim. Só sai o que o servidor mudou (nova leitura das fontes).
    setSecoesEditadas(local =>
      Object.fromEntries(
        Object.entries(local).filter(([chave, naTela]) => {
          const s = (ficha.secoes ?? []).find(x => x.chave === chave);
          return !!s && mesmoTexto(naTela, s);
        }),
      ),
    );
    setSalvamento(s => (s === 'erro' ? s : 'salvo'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);

  const cardId = ficha?.kanban_card_id ?? null;
  const { data: anexosResp } = useQuery({
    queryKey: ['kanban-anexos', cardId],
    queryFn: () => getAnexos(cardId as number),
    enabled: !!cardId,
  });
  const anexos = anexosResp?.data ?? [];

  const tarefa = useTarefa(async (status, erro) => {
    if (status === 'failed') toast.error(erro || 'A leitura das fontes falhou.');
    else toast.success('Ficha montada. Confira os campos antes de gerar.');
    await invalidar(); // os campos só destravam com a ficha nova já na tela
  });

  /** Abre o bloco do campo e põe o cursor nele — espera o elemento existir. */
  const irPara = useCallback(
    (papel: string) => {
      let grupo: string | undefined;
      if (papel.startsWith(PREFIXO_SECAO)) grupo = SECOES;
      else if (papel === ESCOPO || papel === SECOES) grupo = papel;
      else grupo = campos.find(c => c.papel === papel)?.grupo;
      if (grupo) setFechados(f => f.filter(g => g !== grupo));
      let tentativas = 0;
      const procurar = () => {
        const caixa = document.getElementById(`campo-${papel}`);
        const alvo = caixa?.querySelector('input,textarea') as HTMLElement | null;
        if (caixa && alvo && alvo.offsetParent !== null) {
          alvo.focus();
          caixa.scrollIntoView({ block: 'center', behavior: 'smooth' });
          return;
        }
        tentativas += 1;
        if (tentativas < 30) requestAnimationFrame(procurar);
      };
      requestAnimationFrame(procurar);
    },
    [campos],
  );

  const umaVez = async (acao: () => Promise<void>) => {
    if (ocupadoRef.current) return;
    ocupadoRef.current = true;
    setOcupado(true);
    try {
      await acao();
    } finally {
      ocupadoRef.current = false;
      setOcupado(false);
    }
  };

  const lerComIA = () =>
    umaVez(async () => {
      if (!(await salvarAgora())) return;
      try {
        tarefa.acompanhar(await lerFontes.mutateAsync());
      } catch (e) {
        toast.error(mensagemDoErro(e, 'Não foi possível ler as fontes.'));
      }
    });

  const gerarDocumentos = () =>
    umaVez(async () => {
      if (!(await salvarAgora())) return;
      try {
        const r = await gerar.mutateAsync();
        queryClient.invalidateQueries({ queryKey: ['kanban-anexos', cardId] });
        queryClient.invalidateQueries({ queryKey: ['card-contrato'] });
        const erroPacote = r?.data?.pacote?.erro;
        if (erroPacote) {
          toast.warning(
            `Documentos gerados, mas o documento para assinatura não saiu: ${erroPacote}`,
          );
        } else if (r?.data?.aprovado) {
          toast.success('Documentos gerados, sem bloqueios.');
        } else {
          toast.warning('Documentos gerados com bloqueios — veja abaixo.');
        }
      } catch (e) {
        toast.error(mensagemDoErro(e, 'Não foi possível gerar os documentos.'));
      }
    });

  // Com Word corrigido, gerar de novo descarta a correção: confirma antes (passo 3 e dock).
  const pedirGerar = () => {
    const corrigido =
      ficha?.resultado?.documentos.some(d => d.revisado_em) ||
      !!ficha?.resultado?.pacote?.revisado_em;
    if (corrigido) setConfirmarGerar(true);
    else gerarDocumentos();
  };

  // Refazer o documento para assinatura, ou corrigir uma parte, descarta o Word corrigido dele.
  const antesDeRemontar = (acao: () => void) => {
    if (ficha?.resultado?.pacote?.revisado_em) setRemontando(() => acao);
    else acao();
  };

  const refazerDocumentoAssinatura = () =>
    umaVez(async () => {
      // O nome do documento sai da razão social salva.
      if (!(await salvarAgora())) return;
      try {
        const r = await refazerDocumento.mutateAsync();
        queryClient.invalidateQueries({ queryKey: ['kanban-anexos', cardId] });
        queryClient.invalidateQueries({ queryKey: ['card-contrato'] });
        const erroPacote = r?.data?.pacote?.erro;
        if (erroPacote) toast.warning(`O documento para assinatura não saiu: ${erroPacote}`);
        else toast.success('Documento para assinatura refeito.');
      } catch (e) {
        toast.error(mensagemDoErro(e, 'Não foi possível refazer o documento para assinatura.'));
      }
    });

  const escolherWord = (alvo: IVersaoDocumento) => {
    setRevisando(alvo);
    wordRef.current?.click();
  };

  const enviarWord = (alvo: IVersaoDocumento, arquivo: File) =>
    umaVez(async () => {
      try {
        // Como no gerar: o servidor confere o Word contra a ficha salva.
        if (!(await salvarAgora())) return;
        if (alvo.indice === PACOTE) {
          const p = await enviarRevisadoPacote.mutateAsync({ anexoId: alvo.anexoId, arquivo });
          queryClient.invalidateQueries({ queryKey: ['kanban-anexos', cardId] });
          queryClient.invalidateQueries({ queryKey: ['card-contrato'] });
          if (p?.data?.pacote?.aprovado) {
            toast.success(
              'Word corrigido conferido, sem bloqueios. É este que vai para assinatura.',
            );
          } else {
            toast.warning(
              'Word corrigido com bloqueios — veja abaixo. O jurídico pode liberar o envio.',
            );
          }
          return;
        }
        const r = await enviarRevisado.mutateAsync({ ...alvo, arquivo });
        queryClient.invalidateQueries({ queryKey: ['kanban-anexos', cardId] });
        queryClient.invalidateQueries({ queryKey: ['card-contrato'] });
        const erroPacote = r?.data?.pacote?.erro;
        if (erroPacote) {
          toast.warning(
            `Word corrigido conferido, mas o documento para assinatura não saiu: ${erroPacote}`,
          );
        } else if (r?.data?.por_documento?.[alvo.indice]?.aprovado) {
          toast.success(
            'Word corrigido conferido, sem bloqueios. O documento para assinatura foi refeito.',
          );
        } else {
          toast.warning(
            'Word corrigido com bloqueios — veja abaixo. O jurídico pode liberar o envio.',
          );
        }
      } catch (e) {
        toast.error(mensagemDoErro(e, 'Não foi possível enviar o Word corrigido.'));
      } finally {
        setRevisando(null);
      }
    });

  const liberarEnvio = () =>
    umaVez(async () => {
      if (liberando === null) return;
      try {
        const doPacote = liberando.indice === PACOTE;
        const r = doPacote
          ? await liberarPacote.mutateAsync(liberando.anexoId)
          : await liberar.mutateAsync(liberando);
        queryClient.invalidateQueries({ queryKey: ['card-contrato'] });
        if (doPacote || r?.data?.aprovado) {
          toast.success('Envio liberado. Fica registrado quem liberou e quando.');
        } else {
          toast.warning(
            'Documento liberado, mas outro documento ainda trava o envio — veja abaixo.',
          );
        }
      } catch (e) {
        toast.error(mensagemDoErro(e, 'Não foi possível liberar o envio.'));
      } finally {
        setLiberando(null);
      }
    });

  const subir = async (tipo: 'orcamento' | 'cnpj', file?: File) => {
    if (!file || !eventoId || !contratoId) return;
    setEnviando(tipo);
    try {
      await uploadDocumento({ eventoId, file, tipo, contratoId });
      invalidar();
    } catch (e) {
      toast.error(mensagemDoErro(e, 'Não foi possível enviar o arquivo.'));
    } finally {
      setEnviando(null);
    }
  };

  if (!eventoId || !contratoId) {
    return (
      <PageBox>
        <Box p={6}>
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            Faltam os parâmetros do evento e do contrato na URL.
          </Alert>
        </Box>
      </PageBox>
    );
  }

  const voltar = async () => {
    if (await salvarAgora()) navigate(`/eventos/${eventoId}?aba=kanban`);
  };

  const lendo = tarefa.rodando || lerFontes.isPending;
  // Ler substitui a ficha e gerar a recarrega: edição no meio disso se perderia.
  const bloqueado = lendo || gerar.isPending || ocupado;
  const resultado = ficha?.resultado;
  const pacoteAnexo = resultado?.pacote?.anexo_id
    ? anexos.find(a => a.id === resultado.pacote?.anexo_id)
    : undefined;
  // Documento que não saiu ou é de antes da troca do orçamento: fica laranja e pede refazer.
  const documentoParaRefazer = !!(resultado?.pacote?.erro || resultado?.pacote?.desatualizado);
  // Tipo trocado depois de gerar: refazer juntaria os documentos do tipo anterior (o back recusa) — gera de novo.
  const tipoMudou = resultado?.pacote?.desatualizado_por === 'tipo';
  const aviso = resultado ? avisoDoGate(resultado) : undefined;
  // Do documento, não do aviso: o aviso também fica laranja com o documento desatualizado.
  const pacoteCorrigido = !!resultado?.pacote?.revisado_em;
  const liberado = pacoteCorrigido
    ? !!resultado?.pacote?.liberado_em
    : !!resultado?.documentos.some(d => d.liberado_em);
  const corrigidoPor = resultado?.pacote?.revisado_em
    ? `Word corrigido por ${resultado.pacote.revisado_por_nome ?? 'alguém'} em ${quando(
        resultado.pacote.revisado_em,
      )}${
        resultado.pacote.liberado_em
          ? ` · envio liberado por ${resultado.pacote.liberado_por_nome ?? 'alguém'} em ${quando(
              resultado.pacote.liberado_em,
            )}`
          : ''
      }`
    : null;
  const grupos = Array.from(new Set(campos.map(c => c.grupo)));
  const secoes = ficha?.secoes ?? [];
  const blocos = [
    ...grupos,
    ...(ficha?.tem_escopo ? [ESCOPO] : []),
    ...(secoes.length ? [SECOES] : []),
  ];
  const valorDe = (c: ICampoFicha) => edicoes[c.papel] ?? c.valor ?? '';
  const vazio = (c: ICampoFicha) => !valorDe(c).trim();
  const pendencias = campos.filter(c => c.obrigatorio && vazio(c));
  // Seção sem texto o gate barra (o negócio do fornecedor anterior ficaria no documento): conta
  // como "em branco" no bloco e no topo, como os campos obrigatórios. A que a proposta não tem
  // (`removida`) sai do contrato: não é pendência.
  const textoNaTela = (s: ISecaoFicha) => secoesEditadas[s.chave] ?? textoDaSecao(s);
  const secoesVazias = secoes.filter(s => !s.removida && secaoVazia(textoNaTela(s)));
  const emBranco = [
    ...pendencias.map(c => c.papel),
    ...secoesVazias.map(s => papelDaSecao(s.chave)),
  ];
  const obrigatorios = campos.filter(c => c.obrigatorio).length;
  const totalCampo = campos.find(c => c.papel === 'comercial.valor_total');
  const escopo = somaDoEscopo(itens, totalCampo ? valorDe(totalCampo) : null);

  let chipEscopo = { cor: 'green', texto: '✓ soma confere' };
  if (!itens.length) chipEscopo = { cor: 'orange', texto: 'sem itens' };
  else if (escopo.total === null) chipEscopo = { cor: 'gray', texto: `${itens.length} itens` };
  else if (!escopo.bate) chipEscopo = { cor: 'orange', texto: 'soma ≠ valor total' };

  // Antes da 1ª leitura a ação é LER: os campos existem desde o template, então
  // `campos.length` oferecia "Gerar", que dava 409.
  const acao = !ficha?.lido_em ? (
    <Button
      size="sm"
      colorScheme="blue"
      leftIcon={<Sparkle size={14} />}
      isLoading={lendo}
      loadingText="Lendo…"
      isDisabled={!ficha?.pronto || bloqueado}
      onClick={lerComIA}
    >
      Ler fontes com IA
    </Button>
  ) : (
    <Button
      size="sm"
      colorScheme="green"
      isLoading={gerar.isPending}
      loadingText="Gerando…"
      isDisabled={!ficha?.pronto || bloqueado}
      onClick={pedirGerar}
    >
      {resultado?.gerado_em ? 'Gerar de novo' : 'Gerar documentos'}
    </Button>
  );

  return (
    <PageBox>
      <Box p={6} pb={28} maxW="1180px" mx="auto" w="100%">
        <Flex align="center" gap={3} mb={5} wrap="wrap">
          <Button size="sm" variant="ghost" leftIcon={<ArrowLeft size={16} />} onClick={voltar}>
            Voltar ao kanban
          </Button>
          <Heading size="md" fontWeight={600} flex={1}>
            Contrato {ficha?.fornecedor_nome ? `— ${ficha.fornecedor_nome}` : ''}
            {ficha && tipoDoContrato && (
              <Badge ml={2} colorScheme="purple" textTransform="none" fontWeight={500}>
                {tipoDoContrato}
              </Badge>
            )}
          </Heading>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<FileDoc size={14} />}
            colorScheme={ficha?.pronto ? 'green' : 'orange'}
            onClick={() => setTemplateAberto(true)}
          >
            Template do evento {ficha?.pronto ? '✓' : ''}
          </Button>
        </Flex>

        {isLoading && (
          <Flex justify="center" py={16}>
            <Spinner />
          </Flex>
        )}
        {isError && (
          <Alert status="error" borderRadius="md" mb={4}>
            <AlertIcon />
            Não foi possível carregar este contrato. Recarregue a página.
          </Alert>
        )}

        {ficha && !ficha.pronto && (
          <Alert status="warning" borderRadius="md" mb={4}>
            <AlertIcon />
            <Text fontSize="13px" flex={1}>
              {ficha.templates.length
                ? `O template de ${tipoDoContrato.toLowerCase()} do evento ainda não está confirmado.`
                : `O evento não tem template de ${tipoDoContrato.toLowerCase()}.`}{' '}
              Defina uma vez e ele serve todos os fornecedores desse tipo — ou troque o tipo do
              contrato no card do kanban.
            </Text>
            <Button size="sm" colorScheme="orange" onClick={() => setTemplateAberto(true)}>
              Definir template
            </Button>
          </Alert>
        )}

        {ficha && (
          <>
            {/* ── Passo 1: fontes ── */}
            <Box bg="white" borderRadius="12px" p={5} mb={4}>
              <Flex align="center" gap={2} mb={1}>
                <Badge colorScheme="gray">1</Badge>
                <Text fontWeight={600}>Fontes</Text>
              </Flex>
              <Text fontSize="13px" color="gray.500" mb={4}>
                O que o jurídico tem em mãos. A IA lê tudo junto com a consulta da Receita e monta a
                ficha — cada valor com o trecho de onde saiu.
              </Text>
              <Textarea
                value={texto}
                isDisabled={bloqueado}
                onChange={e => {
                  setTexto(e.target.value);
                  mexeu();
                }}
                placeholder="Cole aqui o que o jurídico tem do fornecedor, como no Claude web."
                minH="220px"
                fontSize="13px"
                mb={1}
              />
              <Flex gap={2} mb={3} align="center" wrap="wrap">
                <Text fontSize="12px" color="gray.500">
                  Complete depois dos dois-pontos. O que ficar em branco não é usado.
                </Text>
                {!texto.trim() && (
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="blue"
                    isDisabled={bloqueado}
                    onClick={() => {
                      setTexto(TEXTO_BASE);
                      mexeu();
                    }}
                  >
                    Usar o texto base
                  </Button>
                )}
              </Flex>
              <Flex gap={3} wrap="wrap" align="center">
                <Input
                  size="sm"
                  maxW="240px"
                  placeholder="CNPJ da contratada (se souber)"
                  value={cnpj}
                  isDisabled={bloqueado}
                  onChange={e => {
                    setCnpj(e.target.value);
                    mexeu();
                  }}
                />
                <input
                  ref={refOrcamento}
                  type="file"
                  accept=".pdf,.docx"
                  hidden
                  onChange={e => {
                    subir('orcamento', e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<FileArrowUp size={14} />}
                  isLoading={enviando === 'orcamento'}
                  isDisabled={bloqueado}
                  onClick={() => refOrcamento.current?.click()}
                >
                  Orçamento
                </Button>
                <input
                  ref={refCartao}
                  type="file"
                  accept=".pdf"
                  hidden
                  onChange={e => {
                    subir('cnpj', e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<FileArrowUp size={14} />}
                  isLoading={enviando === 'cnpj'}
                  isDisabled={bloqueado}
                  onClick={() => refCartao.current?.click()}
                >
                  Cartão CNPJ
                </Button>
              </Flex>
              {ficha.fontes.documentos.length > 0 && (
                <Flex gap={2} mt={3} wrap="wrap">
                  {ficha.fontes.documentos.map(d => (
                    <Flex
                      key={d.id}
                      gap={1.5}
                      align="center"
                      bg="gray.50"
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      <FileDoc size={13} color="#38A169" />
                      <Text fontSize="12px" noOfLines={1} maxW="420px">
                        {d.nome}
                      </Text>
                      <Text fontSize="11px" color="gray.400">
                        {d.tipo === 'orcamento' ? 'orçamento' : 'cartão CNPJ'}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              )}
              <Flex mt={4} align="center" gap={3} wrap="wrap">
                <Button
                  colorScheme="blue"
                  leftIcon={<Sparkle size={16} />}
                  isLoading={lendo}
                  loadingText="Lendo as fontes…"
                  isDisabled={!ficha.pronto || bloqueado}
                  onClick={lerComIA}
                >
                  {ficha.lido_em ? 'Ler as fontes de novo' : 'Ler fontes com IA'}
                </Button>
                <Text fontSize="12px" color="gray.500">
                  ~30 s. O que você editou nos campos é mantido; os itens do escopo são lidos de
                  novo do orçamento.
                </Text>
              </Flex>
            </Box>

            {/* ── Passo 2: ficha ── */}
            {campos.length > 0 && (
              <Box bg="white" borderRadius="12px" p={5} mb={4}>
                <Flex align="center" gap={3} mb={1} flexWrap="wrap">
                  <Badge colorScheme="gray">2</Badge>
                  <Text fontWeight={600}>Ficha do fornecedor</Text>
                  <Progress
                    value={
                      obrigatorios ? ((obrigatorios - pendencias.length) / obrigatorios) * 100 : 100
                    }
                    size="sm"
                    w="180px"
                    borderRadius="full"
                    colorScheme={emBranco.length ? 'orange' : 'green'}
                  />
                  <Text fontSize="12px" color="gray.500">
                    {obrigatorios - pendencias.length} de {obrigatorios} obrigatórios
                  </Text>
                </Flex>
                <Text fontSize="13px" color="gray.500" mb={3}>
                  {emBranco.length > 0 ? (
                    <>
                      <b>{emBranco.length} em branco</b> — obrigatório ou seção do negócio em branco
                      fica em branco no documento, e o gate barra.
                    </>
                  ) : (
                    'Tudo preenchido. Confira o que veio das fontes antes de gerar.'
                  )}
                  {ficha.lido_em && ` Lida em ${new Date(ficha.lido_em).toLocaleString('pt-BR')}.`}
                </Text>
                <Flex gap={2} mb={4} flexWrap="wrap">
                  <Button
                    size="sm"
                    colorScheme="orange"
                    variant={emBranco.length ? 'solid' : 'outline'}
                    leftIcon={<ArrowRight size={14} />}
                    isDisabled={emBranco.length === 0}
                    onClick={() => irPara(emBranco[0])}
                  >
                    Próximo em branco
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setFechados(fechados.length ? [] : blocos)}
                  >
                    {fechados.length ? 'Abrir tudo' : 'Fechar tudo'}
                  </Button>
                </Flex>

                {ficha.avisos.length > 0 && (
                  <Box
                    bg="orange.50"
                    border="1px solid"
                    borderColor="orange.100"
                    borderRadius="10px"
                    px={4}
                    py={3}
                    mb={4}
                  >
                    <Text fontSize="12px" fontWeight={600} color="orange.700" mb={1.5}>
                      Avisos da leitura
                    </Text>
                    {ficha.avisos.map(a => (
                      <Flex key={a} gap={2} mb={1} align="flex-start">
                        <Box color="orange.400" mt="2px">
                          <Warning size={13} />
                        </Box>
                        <Text fontSize="12px" color="gray.700">
                          {a}
                        </Text>
                      </Flex>
                    ))}
                  </Box>
                )}

                {/* `reduceMotion`: sem ele o painel anima e o foco do "ir para"
                    cai num elemento ainda escondido. */}
                <Accordion
                  allowMultiple
                  reduceMotion
                  index={blocos.map((b, i) => (fechados.includes(b) ? -1 : i)).filter(i => i >= 0)}
                  onChange={i => {
                    const abertos = Array.isArray(i) ? i : [i];
                    setFechados(blocos.filter((_b, j) => !abertos.includes(j)));
                  }}
                >
                  {grupos.map((g, i) => {
                    const doGrupo = campos.filter(c => c.grupo === g);
                    return (
                      <BlocoFicha
                        key={g}
                        titulo={`2.${i + 1} ${g}`}
                        aberto={!fechados.includes(g)}
                        pendentes={doGrupo.filter(c => c.obrigatorio && vazio(c))}
                        paraConferir={
                          doGrupo.filter(
                            c => !(c.papel in edicoes) && !vazio(c) && c.conferido === false,
                          ).length
                        }
                        resumo={doGrupo
                          .filter(c => !vazio(c))
                          .map(c => ({ papel: c.papel, rotulo: c.rotulo, valor: valorDe(c) }))}
                        irPara={irPara}
                      >
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={5} spacingY={4}>
                          {doGrupo.map(c => (
                            <CampoFicha
                              key={c.papel}
                              campo={c}
                              valor={valorDe(c)}
                              editado={c.papel in edicoes}
                              bloqueado={bloqueado}
                              onChange={v => {
                                setEdicoes(e => ({ ...e, [c.papel]: v }));
                                mexeu();
                              }}
                            />
                          ))}
                        </SimpleGrid>
                      </BlocoFicha>
                    );
                  })}
                  {ficha.tem_escopo && (
                    <BlocoFicha
                      titulo={`2.${grupos.length + 1} Itens do escopo`}
                      aberto={!fechados.includes(ESCOPO)}
                      pendentes={[]}
                      paraConferir={0}
                      resumo={itens.map((it, j) => ({
                        papel: ESCOPO,
                        rotulo: it.descricao || `item ${j + 1}`,
                        valor: [it.quantidade, it.valor].filter(Boolean).join(' · ') || '—',
                      }))}
                      chip={
                        <Badge colorScheme={chipEscopo.cor} fontSize="10px" textTransform="none">
                          {chipEscopo.texto}
                        </Badge>
                      }
                      irPara={irPara}
                    >
                      <Box id={`campo-${ESCOPO}`}>
                        <Text fontSize="12px" color="gray.500" mb={3}>
                          Viram a tabela de escopo do Anexo, no lugar da do fornecedor anterior.
                        </Text>
                        <TabelaEscopo
                          itens={itens}
                          valorTotal={totalCampo ? valorDe(totalCampo) : null}
                          bloqueado={bloqueado}
                          onChange={novo => {
                            setItens(novo);
                            setItensEditados(true);
                            mexeu();
                          }}
                        />
                      </Box>
                    </BlocoFicha>
                  )}
                  {secoes.length > 0 && (
                    <BlocoFicha
                      titulo={`2.${blocos.length} Seções do negócio`}
                      aberto={!fechados.includes(SECOES)}
                      pendentes={secoesVazias.map(s => ({
                        papel: papelDaSecao(s.chave),
                        rotulo: s.titulo,
                      }))}
                      paraConferir={
                        secoes.filter(
                          s =>
                            !editadaNaTela(secoesEditadas, s) &&
                            s.conferido === false &&
                            !secaoVazia(textoNaTela(s)),
                        ).length
                      }
                      resumo={secoes.map(s => ({
                        papel: papelDaSecao(s.chave),
                        rotulo: s.titulo,
                        valor: estadoDaSecao(s, editadaNaTela(secoesEditadas, s)).texto,
                      }))}
                      irPara={irPara}
                    >
                      <Box>
                        <Text fontSize="12px" color="gray.500" mb={3}>
                          O trecho do modelo que muda a cada proposta (produtos e preços, áreas e
                          valor por pessoa, adiantamento). A IA redige na leitura das fontes, só com
                          números que estão nelas; confira antes de gerar.
                        </Text>
                        <SecoesDoNegocio
                          secoes={secoes}
                          editadas={secoesEditadas}
                          bloqueado={bloqueado}
                          onChange={(chave, t) => {
                            setSecoesEditadas(e => ({ ...e, [chave]: t }));
                            mexeu();
                          }}
                        />
                      </Box>
                    </BlocoFicha>
                  )}
                </Accordion>
              </Box>
            )}

            {/* ── Passo 3: documentos ── */}
            {campos.length > 0 && (
              <Box bg="white" borderRadius="12px" p={5}>
                <Flex align="center" gap={2} mb={1}>
                  <Badge colorScheme="gray">3</Badge>
                  <Text fontWeight={600}>Documentos</Text>
                  {resultado?.gerado_em &&
                    (resultado.aprovado ? (
                      <Badge colorScheme={liberado ? 'orange' : 'green'}>
                        {liberado ? 'liberado' : 'sem bloqueios'}
                      </Badge>
                    ) : (
                      <Badge colorScheme="red">{resultado.bloqueios.length} bloqueio(s)</Badge>
                    ))}
                </Flex>
                <Text fontSize="13px" color="gray.500" mb={4}>
                  Gera um documento por template do evento, sem IA, e o documento para assinatura
                  (contrato + anexo + orçamento num Word só). Tudo entra nos anexos do card e segue
                  para assinatura pelo kanban. Achou erro? Baixe o Word, corrija e envie de volta.
                </Text>
                <Button
                  colorScheme="green"
                  mb={4}
                  isLoading={gerar.isPending}
                  isDisabled={!ficha.pronto || bloqueado}
                  onClick={pedirGerar}
                >
                  {resultado?.gerado_em ? 'Gerar de novo' : 'Gerar documentos'}
                </Button>

                {resultado?.gerado_em && (
                  <>
                    <Alert status={aviso?.status} borderRadius="md" mb={3}>
                      <AlertIcon as={aviso?.icone} />
                      <Text fontSize="13px">{aviso?.texto}</Text>
                    </Alert>
                    {resultado.pacote && (
                      <Flex
                        gap={3}
                        align="center"
                        flexWrap="wrap"
                        p={3}
                        mb={2}
                        borderWidth="1px"
                        borderColor={documentoParaRefazer ? 'orange.200' : 'blue.100'}
                        borderRadius="10px"
                      >
                        <FileDoc size={20} color="#3182CE" />
                        <Box flex={1} minW={0}>
                          <Text fontSize="13px" fontWeight={500} noOfLines={1}>
                            {resultado.pacote.nome_arquivo ?? 'Documento para assinatura'}
                          </Text>
                          <Text
                            fontSize="11px"
                            color={documentoParaRefazer ? 'orange.600' : 'gray.500'}
                          >
                            {resultado.pacote.erro ||
                              (resultado.pacote.desatualizado &&
                                (tipoMudou
                                  ? 'O tipo do contrato mudou depois deste documento: gere o contrato de novo.'
                                  : 'O orçamento mudou depois deste documento: refaça antes de enviar.')) ||
                              corrigidoPor ||
                              'contrato + anexo + orçamento num Word só — é este que vai para assinatura (o Autentique gera o PDF)'}
                          </Text>
                        </Box>
                        {!resultado.pacote.erro && (
                          <Badge
                            colorScheme={
                              (!resultado.pacote.aprovado && 'red') ||
                              ((resultado.pacote.desatualizado || liberado) && 'orange') ||
                              'green'
                            }
                            fontSize="10px"
                          >
                            {(!resultado.pacote.aprovado && 'bloqueado') ||
                              (resultado.pacote.desatualizado && 'desatualizado') ||
                              (liberado && 'liberado') ||
                              'ok'}
                          </Badge>
                        )}
                        {documentoParaRefazer && !tipoMudou && (
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="orange"
                            leftIcon={<ArrowsClockwise size={14} />}
                            isLoading={refazerDocumento.isPending}
                            isDisabled={bloqueado}
                            onClick={() => antesDeRemontar(refazerDocumentoAssinatura)}
                          >
                            Refazer documento
                          </Button>
                        )}
                        {pacoteCorrigido && !resultado.pacote.aprovado && (
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="orange"
                            leftIcon={<LockOpen size={14} />}
                            isDisabled={bloqueado}
                            onClick={() =>
                              setLiberando({ indice: PACOTE, anexoId: resultado.pacote?.anexo_id })
                            }
                          >
                            Liberar envio
                          </Button>
                        )}
                        {!resultado.pacote.erro && !!resultado.pacote.anexo_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            leftIcon={<FileArrowUp size={14} />}
                            isLoading={enviarRevisadoPacote.isPending}
                            isDisabled={bloqueado}
                            onClick={() =>
                              escolherWord({ indice: PACOTE, anexoId: resultado.pacote?.anexo_id })
                            }
                          >
                            Word corrigido
                          </Button>
                        )}
                        {pacoteAnexo && (
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="blue"
                            leftIcon={<DownloadSimple size={14} />}
                            onClick={() => window.open(pacoteAnexo.url, '_blank')}
                          >
                            Baixar
                          </Button>
                        )}
                      </Flex>
                    )}
                    {resultado.documentos.map((d, i) => {
                      const anexo = d.anexo_id ? anexos.find(a => a.id === d.anexo_id) : undefined;
                      return (
                        <Flex
                          key={d.anexo_id ?? `${d.template_nome}-${i}`}
                          gap={3}
                          align="center"
                          flexWrap="wrap"
                          p={3}
                          mb={2}
                          borderWidth="1px"
                          borderColor="gray.100"
                          borderRadius="10px"
                        >
                          <FileDoc size={20} color="#3182CE" />
                          <Box flex={1} minW={0}>
                            <Text fontSize="13px" fontWeight={500} noOfLines={1}>
                              {d.nome_arquivo ?? d.template_nome}
                            </Text>
                            <Text fontSize="11px" color="gray.500" noOfLines={2}>
                              {d.revisado_em
                                ? `Word corrigido por ${d.revisado_por_nome ?? 'alguém'} em ${quando(d.revisado_em)}`
                                : `do template ${d.template_nome}`}
                              {d.liberado_em &&
                                ` · envio liberado por ${d.liberado_por_nome ?? 'alguém'} em ${quando(d.liberado_em)}`}
                            </Text>
                          </Box>
                          <Badge
                            colorScheme={
                              (!d.aprovado && 'red') || (d.liberado_em && 'orange') || 'green'
                            }
                            fontSize="10px"
                          >
                            {d.aprovado
                              ? (d.liberado_em && 'liberado') || 'ok'
                              : `${d.bloqueios} bloqueio(s)`}
                          </Badge>
                          {d.revisado_em && !d.aprovado && (
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="orange"
                              leftIcon={<LockOpen size={14} />}
                              isDisabled={bloqueado}
                              onClick={() => setLiberando({ indice: i, anexoId: d.anexo_id })}
                            >
                              Liberar envio
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            leftIcon={<FileArrowUp size={14} />}
                            isLoading={enviarRevisado.isPending && revisando?.indice === i}
                            isDisabled={bloqueado || !d.anexo_id}
                            onClick={() =>
                              antesDeRemontar(() =>
                                escolherWord({ indice: i, anexoId: d.anexo_id }),
                              )
                            }
                          >
                            Word corrigido
                          </Button>
                          {anexo ? (
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="blue"
                              leftIcon={<DownloadSimple size={14} />}
                              onClick={() => window.open(anexo.url, '_blank')}
                            >
                              Baixar
                            </Button>
                          ) : (
                            <Text fontSize="11px" color="gray.400">
                              baixe pelos anexos do card
                            </Text>
                          )}
                        </Flex>
                      );
                    })}
                    {resultado.bloqueios.length > 0 && <Divider my={3} />}
                    {resultado.bloqueios.map((b, i) => (
                      <Flex
                        // o gate emite um item por OCORRENCIA: campo+tipo se repete
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${b.template}:${b.tipo}:${b.campo}:${i}`}
                        gap={2}
                        mb={2}
                        align="flex-start"
                      >
                        <Box color="red.500" mt="2px">
                          <WarningCircle size={14} weight="fill" />
                        </Box>
                        <Box>
                          <Text fontSize="13px" fontWeight={500}>
                            {b.campo}
                            <Text as="span" color="gray.500" fontWeight={400}>
                              {' '}
                              · {b.template}
                            </Text>
                          </Text>
                          <Text fontSize="12px" color="gray.600">
                            {b.detalhe}
                          </Text>
                        </Box>
                      </Flex>
                    ))}
                    {resultado.conferir.length > 0 && (
                      <Box bg="gray.50" borderRadius="10px" px={4} py={3} mt={3}>
                        <Text fontSize="12px" fontWeight={600} color="gray.600" mb={2}>
                          Conferir — não bloqueiam, mas valem uma olhada
                        </Text>
                        {resultado.conferir.map((c, i) => (
                          <Flex
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${c.template}:${c.tipo}:${c.campo}:${i}`}
                            gap={2}
                            mb={1}
                            align="flex-start"
                          >
                            <Box color="orange.400" mt="2px">
                              <Warning size={14} />
                            </Box>
                            <Text fontSize="12px" color="gray.600">
                              <b>{c.campo}</b> — {c.detalhe}
                            </Text>
                          </Flex>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
      {ficha && (
        <DockContrato
          salvamento={salvamento}
          salvoEm={salvoEm}
          onSalvar={() => salvarAgora()}
          pendencias={pendencias.map(c => ({ papel: c.papel, rotulo: c.rotulo, grupo: c.grupo }))}
          irPara={irPara}
          acao={acao}
        />
      )}
      <input
        ref={wordRef}
        type="file"
        accept=".docx"
        hidden
        onChange={e => {
          const arquivo = e.target.files?.[0];
          e.target.value = '';
          if (arquivo && revisando) enviarWord(revisando, arquivo);
        }}
      />
      <DialogModal
        isOpen={confirmarGerar}
        status="warning"
        title="Gerar de novo?"
        description="Há Word corrigido neste contrato. Gerar de novo refaz os documentos a partir da ficha e descarta as correções."
        onConfirm={() => {
          setConfirmarGerar(false);
          gerarDocumentos();
        }}
        onCancel={() => setConfirmarGerar(false)}
        confirmLabel="Gerar de novo"
        cancelLabel="Cancelar"
      />
      <DialogModal
        isOpen={liberando !== null}
        status="warning"
        title="Liberar o envio para assinatura?"
        description="O jurídico revisou este Word e libera o envio mesmo com os pontos que a conferência apontou. Fica registrado quem liberou e quando."
        onConfirm={liberarEnvio}
        onCancel={() => setLiberando(null)}
        confirmLabel="Liberar envio"
        cancelLabel="Cancelar"
      />
      <DialogModal
        isOpen={remontando !== null}
        status="warning"
        title="Descartar o Word corrigido do documento para assinatura?"
        description="O documento para assinatura tem Word corrigido. Refazer o documento, ou corrigir uma parte, monta ele de novo a partir dos documentos e descarta essa correção."
        onConfirm={() => {
          const pendente = remontando;
          setRemontando(null);
          pendente?.();
        }}
        onCancel={() => setRemontando(null)}
        confirmLabel="Continuar"
        cancelLabel="Cancelar"
      />
      <TemplateDoEvento
        eventoId={eventoId}
        tipoInicial={ficha?.contrato_tipo}
        isOpen={templateAberto}
        onClose={() => {
          setTemplateAberto(false);
          invalidar();
        }}
      />
    </PageBox>
  );
}
